import { useState, useRef, useEffect } from "react";
import { Warehouse, ChevronDown, Plus, Pencil, Trash2, Check, X } from "lucide-react";

export function ProjectSwitcher({ projects, activeProjectId, onSwitch, onCreate, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const ref = useRef(null);

  const active = projects.find((p) => p.id === activeProjectId);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
        setRenamingId(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submitCreate = () => {
    const n = newName.trim();
    if (!n) return;
    onCreate(n);
    setNewName("");
    setCreating(false);
    setOpen(false);
  };

  const submitRename = (id) => {
    const n = renameVal.trim();
    if (!n) return;
    onRename(id, n);
    setRenamingId(null);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="project-switcher-button"
        onClick={() => setOpen((o) => !o)}
        title="Switch yard / project"
        className="flex items-center gap-2 border border-[#2E3033] hover:border-[#2560C9] bg-[#0A0A0A] text-white text-xs px-2.5 py-1.5 transition-colors max-w-[190px]"
      >
        <Warehouse className="w-3.5 h-3.5 text-[#2560C9] shrink-0" />
        <span className="font-mono truncate">{active?.name || "Yard"}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#6E7787] shrink-0" />
      </button>

      {open && (
        <div
          data-testid="project-switcher-menu"
          className="absolute left-0 top-full mt-1 w-64 bg-[#16181A] border border-[#2E3033] shadow-[0_8px_30px_rgba(0,0,0,0.6)] z-50"
        >
          <div className="px-3 py-2 border-b border-[#2E3033]">
            <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">Yards</span>
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-row-${p.id}`}
                className={`group flex items-center gap-1 px-2 py-1.5 ${
                  p.id === activeProjectId ? "bg-[#2560C9]/15" : "hover:bg-[#22252A]"
                }`}
              >
                {renamingId === p.id ? (
                  <>
                    <input
                      data-testid="project-rename-input"
                      autoFocus
                      value={renameVal}
                      onChange={(e) => setRenameVal(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submitRename(p.id)}
                      className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2560C9] px-2 py-1 text-white text-xs font-mono focus:outline-none"
                    />
                    <button data-testid="project-rename-confirm" onClick={() => submitRename(p.id)} className="text-[#10B981] hover:text-white p-1">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setRenamingId(null)} className="text-[#6E7787] hover:text-white p-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      data-testid={`project-select-${p.id}`}
                      onClick={() => {
                        if (p.id !== activeProjectId) onSwitch(p.id);
                        setOpen(false);
                      }}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left"
                    >
                      <Warehouse className={`w-3.5 h-3.5 shrink-0 ${p.id === activeProjectId ? "text-[#2560C9]" : "text-[#6E7787]"}`} />
                      <span className="font-mono text-xs text-white truncate">{p.name}</span>
                    </button>
                    <button
                      data-testid={`project-rename-${p.id}`}
                      onClick={() => { setRenamingId(p.id); setRenameVal(p.name); }}
                      className="opacity-0 group-hover:opacity-100 text-[#6E7787] hover:text-[#2560C9] p-1 transition-opacity"
                      title="Rename yard"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {projects.length > 1 && (
                      <button
                        data-testid={`project-delete-${p.id}`}
                        onClick={() => {
                          if (window.confirm(`Delete "${p.name}" and all its saved days? This cannot be undone.`)) onDelete(p.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-[#6E7787] hover:text-[#EF4444] p-1 transition-opacity"
                        title="Delete yard"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-[#2E3033] p-2">
            {creating ? (
              <div className="flex items-center gap-1">
                <input
                  data-testid="project-new-input"
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitCreate()}
                  placeholder="New yard name"
                  className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2560C9] px-2 py-1.5 text-white text-xs font-mono placeholder-[#6E7787] focus:outline-none"
                />
                <button data-testid="project-new-confirm" onClick={submitCreate} className="bg-[#2560C9] hover:bg-[#3B75DF] text-white p-1.5">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setCreating(false); setNewName(""); }} className="border border-[#2E3033] text-[#6E7787] hover:text-white p-1.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                data-testid="project-new-button"
                onClick={() => setCreating(true)}
                className="w-full flex items-center justify-center gap-1.5 border border-dashed border-[#2E3033] hover:border-[#2560C9] text-[#A0AABF] hover:text-white text-xs font-mono py-1.5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> New Yard
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
